'use client'

import Image, { ImageProps } from 'next/image'

import staticPlaceholdersData from '@/data/static-placeholders.json'
import { Media } from '@/payload-types'
import { extractPlainText, RichTextType } from '@/utilities/extractPlainText'
import { generateImageSizes } from '@/utilities/generateImageSizes'
import { getMediaUrl } from '@/utilities/getMediaUrl'
import { cn } from '@/utilities/ui'

/** Used when a Payload upload is missing stored dimensions (e.g. seeded media). */
const FALLBACK_WIDTH = 800
const FALLBACK_HEIGHT = 600

function positiveDimension(value: number | null | undefined): number | undefined {
  return typeof value === 'number' && value > 0 ? value : undefined
}

const isObject = <T,>(value: unknown): value is T => {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

const getImageAlt = (image: Media) => {
  return image.alt ?? extractPlainText(image.caption as RichTextType) ?? ''
}

const staticPlaceholders: Record<string, string> =
  typeof staticPlaceholdersData === 'object' &&
  staticPlaceholdersData !== null &&
  !Array.isArray(staticPlaceholdersData)
    ? (staticPlaceholdersData as Record<string, string>)
    : {}

function getFilenameFromPath(pathOrUrl: string): string | null {
  const segment = pathOrUrl.split('/').filter(Boolean).pop()
  return segment ?? null
}

/** Blur placeholder for `public/media` assets, looked up by filename. */
function getStaticPlaceholder(pathOrUrl: string): string | undefined {
  if (!pathOrUrl.startsWith('/media/') && !pathOrUrl.startsWith('media/')) return undefined
  return staticPlaceholders[getFilenameFromPath(pathOrUrl) ?? '']
}

type ImageContext = 'icon' | 'thumbnail' | 'card' | 'hero' | 'full'

const CONTEXT_SIZES: Record<ImageContext, string> = {
  icon: '30px',
  thumbnail: '300px',
  hero: '100vw',
  full: '100vw',
  card: generateImageSizes([
    ['sm', '1/2'],
    ['lg', '1/3'],
  ]),
}

interface PayloadImageClientProps
  extends Omit<ImageProps, 'src' | 'alt' | 'resource' | 'width' | 'height'> {
  resource: Media | string | number | null | undefined
  context?: ImageContext
  width?: number
  height?: number
  alt?: string
  priority?: boolean
  style?: React.CSSProperties
  imgClassName?: string
  sizes?: string
  pictureClassName?: string
  disableCustomLoader?: boolean
}

export const ImageMediaClient = ({
  context = 'card',
  className,
  fill = false,
  loading: loadingFromProps,
  priority,
  resource,
  width: widthFromProps,
  height: heightFromProps,
  alt: altFromProps,
  style: styleFromProps,
  sizes: sizesFromProps,
  onLoad: onLoadFromProps,
  imgClassName,
  pictureClassName,
  disableCustomLoader: _disableCustomLoader,
  ...props
}: PayloadImageClientProps) => {
  /** `loading` must be omitted when `priority` is set; otherwise default to lazy. */
  const loading = priority ? undefined : (loadingFromProps ?? 'lazy')
  const sizes = sizesFromProps ?? CONTEXT_SIZES[context]

  if (typeof resource === 'string') {
    const blurDataURL = getStaticPlaceholder(resource)

    return (
      <Image
        src={resource}
        alt={altFromProps ?? ''}
        fill={fill}
        sizes={fill ? (sizesFromProps ?? '100vw') : undefined}
        width={!fill ? (widthFromProps ?? undefined) : undefined}
        height={!fill ? (heightFromProps ?? undefined) : undefined}
        className={cn(imgClassName ?? className)}
        onLoad={onLoadFromProps}
        priority={priority}
        loading={loading}
        {...(blurDataURL ? { placeholder: 'blur' as const, blurDataURL } : {})}
        {...props}
      />
    )
  }

  if (!isObject<Media>(resource)) return null

  let width = positiveDimension(widthFromProps) ?? positiveDimension(resource.width)
  let height = positiveDimension(heightFromProps) ?? positiveDimension(resource.height)

  if (!fill) {
    width = width ?? FALLBACK_WIDTH
    height = height ?? FALLBACK_HEIGHT
  }

  const src = getMediaUrl(resource.url, resource.updatedAt)

  if (!src) return null

  const objectPosition =
    typeof resource.focalX === 'number' && typeof resource.focalY === 'number'
      ? `${Math.round(resource.focalX)}% ${Math.round(resource.focalY)}%`
      : undefined

  const altText = getImageAlt(resource)

  const blurDataURL =
    (resource as Media & { placeholderBase64?: string | null })?.placeholderBase64 ??
    getStaticPlaceholder(resource.url ?? '') ??
    DEFAULT_PLACEHOLDER_BLUR

  return (
    <picture className={cn(pictureClassName)}>
      <Image
        src={src}
        alt={altFromProps ?? altText ?? ''}
        fill={fill}
        width={!fill ? width : undefined}
        height={!fill ? height : undefined}
        sizes={sizes}
        priority={priority}
        loading={loading}
        placeholder="blur"
        blurDataURL={blurDataURL}
        className={cn(imgClassName ?? className)}
        onLoad={onLoadFromProps}
        style={{
          objectPosition,
          ...styleFromProps,
        }}
        {...props}
      />
    </picture>
  )
}

/** Fallback blur placeholder for images without a stored base64 value */
const DEFAULT_PLACEHOLDER_BLUR =
  'data:image/png;base64,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'
